<template>
  <div class="reporting-section">
    <div class="section-header">
      <i class="material-icons section-icon">visibility</i>
      <h2>{{ $t('dash.reporting.section.impressions.title') }}</h2>
    </div>

    <!-- LinkedIn-specific metrics -->
    <template v-if="network === 'linkedin'">
      <div class="metric-row">
        <MetricCard
          :value="stats.page.totalImpressions || 0"
          :previous-value="prev('totalImpressions')"
          :label="$t('dash.reporting.impression')"
          icon="remove_red_eye"
        />
        <MetricCard
          v-if="stats.page.totalUniqueImpressions != null"
          :value="stats.page.totalUniqueImpressions || 0"
          :previous-value="prev('totalUniqueImpressions')"
          :label="$t('dash.reporting.unique_impression')"
          icon="visibility_off"
        />
        <MetricCard
          v-if="stats.page.page_views != null"
          :value="stats.page.page_views || 0"
          :previous-value="prev('page_views')"
          :label="$t('dash.reporting.page_views')"
          icon="web"
        />
        <MetricCard
          v-if="stats.page.pageCustomButtonClickCounts != null"
          :value="stats.page.pageCustomButtonClickCounts || 0"
          :previous-value="prev('pageCustomButtonClickCounts')"
          :label="$t('dash.reporting.pageCustomButtonClickCounts')"
          icon="touch_app"
        />
      </div>

      <template v-if="liImpressionsGraphData.length && liImpressionsLabels.length">
        <div class="graph-title">{{ $t('dash.reporting.impression') }}</div>
        <div class="graph-wrap">
          <Graph
            :id="`impressions-li-${graphId}`"
            :data="liImpressionsGraphData"
            :labels="liImpressionsLabels"
            height="220px"
            width="100%"
          />
        </div>
      </template>

      <template v-if="liPageGraphData.length && liPageLabels.length">
        <div class="graph-title">{{ $t('dash.reporting.page_views') }}</div>
        <div class="graph-wrap">
          <Graph
            :id="`pageviews-li-${graphId}`"
            :data="liPageGraphData"
            :labels="liPageLabels"
            height="220px"
            width="100%"
          />
        </div>
      </template>
    </template>

    <!-- Facebook / other networks -->
    <template v-else>
      <div class="metric-row">
        <MetricCard
          v-if="totalPageViews != null"
          :value="totalPageViews"
          :previous-value="prev('page_views_total')"
          :label="$t('dash.reporting.page_views')"
          icon="web"
        />
        <MetricCard
          :value="stats.page.totalImpressions || 0"
          :previous-value="prev('totalImpressions')"
          :label="$t('dash.reporting.impression')"
          icon="remove_red_eye"
        />
        <MetricCard
          v-if="totalReach != null"
          :value="totalReach"
          :previous-value="prev('totalReach')"
          :label="$t('dash.label.fb.impressions_reach')"
          icon="people"
        />
        <Tooltip v-if="totalOrganic != null" :text="$t('dash.reporting.tooltip.impressions_organic')" position="top">
          <MetricCard
            :value="totalOrganic"
            :previous-value="prev('page_impressions_organic_v2') != null ? prev('page_impressions_organic_v2') : prev('page_impressions_organic')"
            :label="$t('dash.label.fb.impressions_organic')"
            icon="eco"
          />
        </Tooltip>
        <Tooltip v-if="totalPaid != null" :text="$t('dash.reporting.tooltip.impressions_paid')" position="top">
          <MetricCard
            :value="totalPaid"
            :previous-value="prev('page_impressions_paid')"
            :label="$t('dash.label.fb.impressions_paid')"
            icon="monetization_on"
          />
        </Tooltip>
        <Tooltip v-if="totalViral != null" :text="$t('dash.reporting.tooltip.impressions_viral')" position="top">
          <MetricCard
            :value="totalViral"
            :previous-value="prev('page_impressions_viral')"
            :label="$t('dash.label.fb.impressions_viral')"
            icon="share"
          />
        </Tooltip>
      </div>

      <template v-if="mainGraphData.length && graphLabels.length">
        <div class="graph-title">{{ $t('dash.reporting.impression') }}</div>
        <div class="graph-wrap">
          <Graph
            :id="`impressions-main-${graphId}`"
            :data="mainGraphData"
            :labels="graphLabels"
            height="220px"
            width="100%"
          />
        </div>
      </template>

      <template v-if="reachGraphData.length && reachLabels.length">
        <div class="graph-title">{{ $t('dash.label.fb.impressions_reach') }}</div>
        <div class="graph-wrap">
          <Graph
            :id="`impressions-reach-${graphId}`"
            :data="reachGraphData"
            :labels="reachLabels"
            height="220px"
            width="100%"
          />
        </div>
      </template>

      <template v-if="breakdownGraphData.length && breakdownLabels.length">
        <div class="graph-title">{{ $t('dash.reporting.section.impressions.subtitle') }}</div>
        <div class="graph-wrap">
          <Graph
            :id="`impressions-breakdown-${graphId}`"
            :data="breakdownGraphData"
            :labels="breakdownLabels"
            height="220px"
            width="100%"
          />
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import MetricCard from './panels/MetricCard.vue'
import Graph from './Graph.vue'
import Tooltip from '../tooltip/tooltipInit.vue'
import moment from 'moment'

function sortByDate(arr) {
  return arr.slice().sort((a, b) => a.date - b.date)
}
function toLabels(arr) {
  return sortByDate(arr).map(d => moment(d.date * 1000).format('MM/DD/YYYY'))
}
function sumArr(val) {
  if (val == null) return null
  if (!Array.isArray(val)) return typeof val === 'number' ? val : null
  if (!val.length) return null
  return val.reduce((s, d) => s + (d.total || 0), 0)
}
function toGraphArr(arr) {
  if (!Array.isArray(arr) || !arr.length) return []
  return sortByDate(arr).map(d => d.total || 0)
}

const COLOR_PAGEVIEWS = '#3B82F6'
const COLOR_ORGANIC   = '#10B981'
const COLOR_PAID      = '#F59E0B'
const COLOR_VIRAL     = '#EF4444'
const COLOR_REACH     = '#A855F7'

export default {
  name: 'ReportingImpressions',
  components: { MetricCard, Graph, Tooltip },
  props: {
    stats:       { type: Object, required: true },
    graphLabels: { type: Array,  default: () => [] },
    page:        { type: Object, default: null },
  },
  computed: {
    graphId()  { return this.page ? this.page.id : 'impressions' },
    network()  { return this.page ? (this.page.network || '') : '' },
    totalPageViews() { return sumArr(this.stats.page.page_views_total) },
    totalReach()     { return sumArr(this.stats.page.totalReach) },
    totalOrganic()   { return sumArr(this.stats.page.page_impressions_organic_v2 || this.stats.page.page_impressions_organic) },
    totalPaid()      { return sumArr(this.stats.page.page_impressions_paid) },
    totalViral()     { return sumArr(this.stats.page.page_impressions_viral) },

    // LinkedIn impression time-series graphs
    liImpressionsLabels() {
      const base = this.stats.page.impression_posts_graph
      return Array.isArray(base) && base.length ? toLabels(base) : []
    },
    liImpressionsGraphData() {
      const impressions = toGraphArr(this.stats.page.impression_posts_graph)
      const unique      = toGraphArr(this.stats.page.page_unique_impressions_graph)
      const datasets = []
      if (impressions.length) datasets.push({ label: this.$t('dash.reporting.impression'),        data: impressions, type: 'line', color: '#6B1F4D' })
      if (unique.length)      datasets.push({ label: this.$t('dash.reporting.unique_impression'), data: unique,      type: 'line', color: '#A855F7' })
      return datasets
    },

    // LinkedIn page views + custom button clicks graphs
    liPageLabels() {
      const base = this.stats.page.page_views_graph || this.stats.page.page_custom_button_graph
      return Array.isArray(base) && base.length ? toLabels(base) : []
    },
    liPageGraphData() {
      const views   = toGraphArr(this.stats.page.page_views_graph)
      const unique  = toGraphArr(this.stats.page.page_unique_views_graph)
      const custom  = toGraphArr(this.stats.page.page_custom_button_graph)
      const datasets = []
      if (views.length)  datasets.push({ label: this.$t('dash.reporting.page_views'),                    data: views,  type: 'line', color: '#3B82F6' })
      if (unique.length) datasets.push({ label: this.$t('dash.reporting.unique_impression'),             data: unique, type: 'line', color: '#60A5FA' })
      if (custom.length) datasets.push({ label: this.$t('dash.reporting.pageCustomButtonClickCounts'),   data: custom, type: 'bar',  color: '#F59E0B' })
      return datasets
    },

    mainGraphData() {
      const datasets = []
      const impressions = toGraphArr(this.stats.page.impression_posts_graph)
      if (impressions.length) datasets.push({ label: this.$t('dash.reporting.impression'), data: impressions, type: 'line' })
      const posts = toGraphArr(this.stats.page.nb_post_graph)
      if (posts.length) datasets.push({ label: this.$t('dash.reporting.publications'), data: posts, type: 'bar' })
      return datasets
    },

    reachLabels() {
      const arr = this.stats.page.daily_reach_graph
      return Array.isArray(arr) && arr.length ? toLabels(arr) : []
    },
    reachGraphData() {
      const reach = toGraphArr(this.stats.page.daily_reach_graph)
      if (!reach.length) return []
      return [{ label: this.$t('dash.label.fb.impressions_reach'), data: reach, type: 'line', color: COLOR_REACH }]
    },

    breakdownLabels() {
      const candidates = [
        this.stats.page.page_impressions_organic_v2,
        this.stats.page.page_impressions_organic,
        this.stats.page.page_impressions_paid,
        this.stats.page.page_impressions_viral,
        this.stats.page.page_views_total,
      ]
      for (const arr of candidates) {
        if (Array.isArray(arr) && arr.length) return toLabels(arr)
      }
      return []
    },

    breakdownGraphData() {
      const datasets = []
      const organicArr = this.stats.page.page_impressions_organic_v2 || this.stats.page.page_impressions_organic
      const organic  = Array.isArray(organicArr) ? toGraphArr(organicArr) : []
      const paid     = Array.isArray(this.stats.page.page_impressions_paid)  ? toGraphArr(this.stats.page.page_impressions_paid)  : []
      const viral    = Array.isArray(this.stats.page.page_impressions_viral) ? toGraphArr(this.stats.page.page_impressions_viral) : []
      const views    = Array.isArray(this.stats.page.page_views_total)       ? toGraphArr(this.stats.page.page_views_total)       : []
      if (viral.length)   datasets.push({ label: this.$t('dash.label.fb.impressions_viral'),        data: viral,   type: 'line', color: COLOR_VIRAL })
      if (paid.length)    datasets.push({ label: this.$t('dash.label.fb.impressions_paid'),         data: paid,    type: 'line', color: COLOR_PAID })
      if (organic.length) datasets.push({ label: this.$t('dash.label.fb.impressions_organic'),      data: organic, type: 'line', color: COLOR_ORGANIC })
      if (views.length)   datasets.push({ label: this.$t('dash.reporting.page_views'),              data: views,   type: 'line', color: COLOR_PAGEVIEWS })
      return datasets
    },
  },
  methods: {
    prev(field) {
      const p = this.stats.page.previous_period
      if (!p || p[field] == null) return null
      const v = p[field]
      // previous_period peut contenir des séries (arrays) — on les somme
      return Array.isArray(v) ? v.reduce((s, d) => s + (d.total || 0), 0) : v
    },
  },
}
</script>

<style scoped>
.reporting-section {
  background: #fff;
  border-radius: 5.6px;
  padding: 20px 24px;
  box-shadow: 0 2px 5.6px rgba(107,31,77,0.06);
  border: 1px solid #f1edf5;
}
.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
}
.section-icon { color: #6B1F4D; font-size: 22px; }
.section-header h2 { font-size: 16px; font-weight: 700; color: #121826; margin: 0; }
.metric-row { display: flex; flex-wrap: wrap; gap: 14px; margin-bottom: 20px; }
.graph-title {
  font-size: 12px;
  font-weight: 600;
  color: #9ba3b7;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin: 16px 0 6px;
}
.graph-wrap { width: 100%; margin-bottom: 8px; }
</style>
